/**
 * Sidebar Component - Simplified for Referrals-Only App
 * Side navigation menu with referral management
 */

import { useState, useEffect, useCallback } from 'react';
import { Layout, Menu } from 'antd';
import {
  DashboardOutlined,
  FileTextOutlined,
  InboxOutlined,
  BankOutlined,
  EnvironmentOutlined,
  SafetyOutlined,
  MedicineBoxOutlined,
  ClockCircleOutlined,
  InsuranceOutlined,
  FileOutlined,
  ProfileOutlined,
  TeamOutlined,
  CalendarOutlined,
  UserOutlined,
  StarOutlined,
  CustomerServiceOutlined,
} from '@ant-design/icons';
import UboraHealthLogo from '../../../assets/Ubora_Health_Logo.svg';
import type { MenuProps } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import { PATHS } from '../../../routes/constants/paths';
import './Sidebar.scss';

const { Sider } = Layout;

type MenuItem = Required<MenuProps>['items'][number];

/**
 * Create menu item helper
 */
function getItem(
  label: React.ReactNode,
  key: string,
  icon?: React.ReactNode,
  children?: MenuItem[],
  disabled?: boolean
): MenuItem {
  return {
    key,
    icon,
    children,
    label,
    disabled,
  } as MenuItem;
}

/**
 * Sidebar Component
 */
export const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);

  // Get open keys for expanded submenu based on the current route.
  // Memoized so it doesn't change identity on every render (which can reset `openKeys`).
  const getOpenKeys = useCallback((): string[] => {
    const path = location.pathname;
    const keys: string[] = [];

    if (path.startsWith('/agency')) keys.push('agency-menu');
    if (path.startsWith('/caregivers') || path.startsWith('/caregiver') || path.startsWith('/staffing')) {
      keys.push('caregiver-menu');
    }

    return keys;
  }, [location.pathname]);

  // Initialize openKeys based on current location
  const [openKeys, setOpenKeys] = useState<string[]>(() => getOpenKeys());

  // Menu items with Agency Onboarding
  const menuItems: MenuItem[] = [
    getItem('Dashboard', PATHS.DASHBOARD, <DashboardOutlined />, undefined, false),
    getItem('Care Requests', PATHS.SERVICE_REQUESTS, <InboxOutlined />),
    getItem('Help & Support', PATHS.TICKETS, <CustomerServiceOutlined />),
    getItem('Patients', PATHS.PATIENTS, <UserOutlined />),
    getItem('Referrals', PATHS.REFERRALS, <FileTextOutlined />),
    getItem('Patient Reviews', PATHS.REVIEWS, <StarOutlined />),
    getItem('Staff', 'caregiver-menu', <TeamOutlined />, [
      getItem('All Staff Members', PATHS.CAREGIVERS_LIST, <TeamOutlined />),
      getItem('Staffing Board', PATHS.STAFFING, <CalendarOutlined />),
    ]),
    getItem('Agency Setup', 'agency-menu', <BankOutlined />, [
      getItem(<span className="step-label"><span className="step-num">1</span>Business Info</span>, PATHS.AGENCY_ONBOARDING_STEP(1), <BankOutlined />),
      getItem(<span className="step-label"><span className="step-num">2</span>Profile & Marketing</span>, PATHS.AGENCY_ONBOARDING_STEP(2), <ProfileOutlined />),
      getItem(<span className="step-label"><span className="step-num">3</span>Office Address</span>, PATHS.AGENCY_ONBOARDING_STEP(3), <EnvironmentOutlined />),
      getItem(<span className="step-label"><span className="step-num">4</span>Licenses</span>, PATHS.AGENCY_ONBOARDING_STEP(4), <SafetyOutlined />),
      getItem(<span className="step-label"><span className="step-num">5</span>Services & Areas</span>, PATHS.AGENCY_ONBOARDING_STEP(5), <MedicineBoxOutlined />),
      getItem(<span className="step-label"><span className="step-num">6</span>Office Hours</span>, PATHS.AGENCY_ONBOARDING_STEP(6), <ClockCircleOutlined />),
      getItem(<span className="step-label"><span className="step-num">7</span>Insurance & Payers</span>, PATHS.AGENCY_ONBOARDING_STEP(7), <InsuranceOutlined />),
      getItem(<span className="step-label"><span className="step-num">8</span>Upload Documents</span>, PATHS.AGENCY_ONBOARDING_STEP(8), <FileOutlined />),
    ]),
  ];

  const handleMenuClick: MenuProps['onClick'] = (e) => {
    const targetPath = e.key as string;
    
    // Skip navigation for group headers
    if (targetPath === 'agency-menu' || targetPath === 'caregiver-menu') {
      return;
    }
    
    // Special handling for Dashboard
    if (targetPath === PATHS.DASHBOARD || targetPath === '/dashboard') {
      navigate(PATHS.DASHBOARD, { replace: false });
      return;
    }
    
    // Navigate to the target path - ensure it's an absolute path
    if (targetPath && targetPath.startsWith('/')) {
      navigate(targetPath, { replace: false });
    } else if (targetPath) {
      // If path doesn't start with /, prepend it
      const prefixedPath = `/${targetPath}`;
      navigate(prefixedPath, { replace: false });
    }
  };

  // Get selected key based on current location
  const getSelectedKey = (): string => {
    const path = location.pathname;
    
    // Match agency onboarding routes
    if (path.startsWith('/agency/onboarding')) {
      const stepMatch = path.match(/\/agency\/onboarding\/step\/(\d+)/);
      if (stepMatch) {
        const step = parseInt(stepMatch[1]);
        const selectedKey = PATHS.AGENCY_ONBOARDING_STEP(step);
        return selectedKey;
      }
      const selectedKey = PATHS.AGENCY_ONBOARDING;
      return selectedKey;
    }

    // Match caregiver onboarding routes
    if (path.startsWith('/caregiver/onboarding')) {
      const stepMatch = path.match(/\/caregiver\/onboarding\/.*\/step\/(\d+)/);
      if (stepMatch) {
        const selectedKey = `/caregiver/onboarding/step/${stepMatch[1]}`;
        return selectedKey;
      }
    }

    // Match staffing board
    if (path.startsWith('/staffing')) {
      return PATHS.STAFFING;
    }

    // Match caregiver list/detail routes
    if (path.startsWith('/caregivers')) {
      return PATHS.CAREGIVERS_LIST;
    }
    
    // Match agency profile routes
    if (path.startsWith('/agency')) {
      const selectedKey = PATHS.AGENCY_PROFILE;
      return selectedKey;
    }
    
    // Match referral routes
    if (path.startsWith('/referrals')) {
      return PATHS.REFERRALS;
    }

    // Match patients routes
    if (path.startsWith('/patients')) {
      return PATHS.PATIENTS;
    }

    // Match reviews routes
    if (path.startsWith('/reviews')) {
      return PATHS.REVIEWS;
    }

    // Match service request routes
    if (path.startsWith('/service-requests')) {
      return PATHS.SERVICE_REQUESTS;
    }

    // Match tickets routes
    if (path.startsWith('/tickets')) {
      return path === '/tickets' || path === '/tickets/' ? PATHS.TICKETS : path;
    }
    
    // Match dashboard
    if (path === '/' || path === '/dashboard') {
      const selectedKey = PATHS.DASHBOARD;
      return selectedKey;
    }
    
    return path;
  };

  // Track location changes to verify navigation
  useEffect(() => {
    // Update openKeys when location changes to keep submenus open for current route
    setOpenKeys(getOpenKeys());
  }, [getOpenKeys]);

  return (
    <Sider
      collapsible
      collapsed={collapsed}
      onCollapse={(value) => setCollapsed(value)}
      className="app-sidebar"
      width={260}
      collapsedWidth={64}
      theme="light"
    >
      {/* Logo Section */}
      <div className="app-sidebar__logo">
        <div className="app-sidebar__logo-container">
          <img
            src={UboraHealthLogo}
            alt="Ubora Health"
            className="app-sidebar__logo-img"
          />
          {!collapsed && (
            <div className="app-sidebar__logo-text">
              <div className="app-sidebar__logo-title">UboraHealth</div>
              <div className="app-sidebar__logo-subtitle">Admin Portal</div>
            </div>
          )}
        </div>
      </div>

      {/* Menu */}
      <div className="app-sidebar__menu-container">
        <Menu
          mode="inline"
          selectedKeys={[getSelectedKey()]}
          openKeys={openKeys}
          items={menuItems}
          onClick={handleMenuClick}
          onOpenChange={(keys) => {
            setOpenKeys(keys);
          }}
          triggerSubMenuAction="click"
          className="app-sidebar__menu"
          theme="light"
        />
      </div>
    </Sider>
  );
};

export default Sidebar;
